import { CSRF_HEADER } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";

/**
 * The title an answer is saved under (spec `saved-answer-titles`): the server's suggestion
 * fills it once it arrives, unless the user typed first. Nothing waits for the suggestion, and
 * a failed one leaves the field as it is.
 */
export function useSuggestedTitle(props: {
  conversationId: string;
  messageId: string;
  sink: "file" | "wiki";
  /** The picked project of a standalone conversation; else null. */
  target: string | null;
  /** False while a standalone conversation has no target yet. */
  enabled?: boolean;
  /** What the user typed, when it must outlive this hook's component (a target change). */
  typed?: [string | null, (value: string | null) => void];
}) {
  const { conversationId, messageId, sink, target } = props;
  const { csrfToken } = useSession();
  const suggestion = useQuery({
    // A metered call: once per message and target while the page lives, kept after the dialog
    // closes. Not per sink: the title is the same for a file and a draft (the target, though,
    // may narrow the models the server can use).
    queryKey: ["save-title", conversationId, messageId, target],
    // A heuristic answer (timeout, spend limit) is asked again on the next open.
    staleTime: (query) => (query.state.data?.source === "model" ? Number.POSITIVE_INFINITY : 0),
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    enabled: props.enabled ?? true,
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].messages[":messageId"].title.$post(
          {
            param: { conversationId, messageId },
            json: { sink, ...(target ? { targetProjectId: target } : {}) },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
  });
  const own = useState<string | null>(null);
  const [typed, setTyped] = props.typed ?? own;
  return {
    value: typed ?? suggestion.data?.title ?? "",
    onChange: setTyped,
    suggesting: typed === null && suggestion.isFetching,
  };
}

/** The title field of a save dialog, with a quiet line while the suggestion is on its way. */
export function SuggestedTitleField(props: {
  label: string;
  placeholder: string;
  title: ReturnType<typeof useSuggestedTitle>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { title } = props;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${id}-title`}>{props.label}</Label>
      <Input
        id={`${id}-title`}
        value={title.value}
        maxLength={200}
        placeholder={props.placeholder}
        aria-describedby={`${id}-hint`}
        onChange={(e) => title.onChange(e.currentTarget.value)}
      />
      <p id={`${id}-hint`} aria-live="polite" className="min-h-4 text-xs text-fg-muted">
        {title.suggesting ? t("save.suggesting") : ""}
      </p>
    </div>
  );
}
